import type { MessageTemplate } from "@repo/app-types/entities/messageTemplate";
import {
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

const messageTemplateApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Message Template endpoints
    allMessageTemplates: builder.query<MessageTemplate[], void>({
      query: () => "/message-templates",
      providesTags: (result) =>
        getTagsProvidedByList(TagType.MessageTemplate, result),
    }),
    createMessageTemplate: builder.mutation<
      MessageTemplate,
      Pick<MessageTemplate, "type" | "subject" | "body">
    >({
      query: (values) => ({
        method: "POST",
        url: "/message-templates",
        body: values,
      }),
      invalidatesTags: () =>
        getTagsInvalidatedByCreate(TagType.MessageTemplate),
    }),
    updateMessageTemplate: builder.mutation<
      MessageTemplate,
      Pick<MessageTemplate, "id"> &
        Partial<Pick<MessageTemplate, "type" | "subject" | "body">>
    >({
      query: (values) => ({
        method: "PATCH",
        url: `/message-templates/${encodeURIComponent(values.id)}`,
        body: values,
      }),
      invalidatesTags: (result, error, { id }) =>
        getTagsInvalidatedByUpdate(TagType.MessageTemplate, id),
    }),
    deleteMessageTemplate: builder.mutation<void, string>({
      query: (id) => ({
        method: "DELETE",
        url: `/message-templates/${encodeURIComponent(id)}`,
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          messageTemplateApi.util.updateQueryData(
            "allMessageTemplates",
            undefined,
            (draft) => {
              const index = draft.findIndex((template) => template.id === id);
              if (index !== -1) {
                draft.splice(index, 1);
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (result, error, id) =>
        getTagsInvalidatedByDelete(TagType.MessageTemplate, id),
    }),
  }),
});

export const {
  useAllMessageTemplatesQuery,
  useCreateMessageTemplateMutation,
  useUpdateMessageTemplateMutation,
  useDeleteMessageTemplateMutation,
} = messageTemplateApi;
